import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LOGO_URL } from "@/lib/tps";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(username.trim(), password);
      toast.success(`Welcome, ${u.name}`);
      nav(u.role === "owner" ? "/" : "/production");
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (Array.isArray(d) ? d.map((x) => x.msg).join(", ") : "Login failed");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" style={{ background: "#FDFBF7" }}>
      {/* Left brand panel — soft cream with subtle pink wash, logo centered */}
      <div className="lg:w-1/2 lg:min-h-screen flex items-center justify-center p-8 lg:p-12 relative"
        style={{
          background:
            "radial-gradient(ellipse at center, #F3E8E9 0%, #FDFBF7 70%)",
        }}
      >
        <div className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 25% 25%, #B86F77 1px, transparent 1.5px), radial-gradient(circle at 75% 75%, #B86F77 1px, transparent 1.5px)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="relative text-center">
          <div className="mx-auto rounded-2xl overflow-hidden bg-[#C77D85] shadow-xl ring-4 ring-white/70 outline outline-2 outline-[#B86F77]/40"
            style={{ width: 240, height: 240 }}
          >
            <img src={LOGO_URL} alt="The Palate Stories" className="w-full h-full object-cover" />
          </div>
          <div className="mt-6 font-heading text-3xl text-[#141E30]">The Palate Stories</div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#B86F77] mt-1.5">Patisserie & Bake Studio</div>
          <p className="text-[#4B5563] mt-5 max-w-sm mx-auto text-sm">
            Bespoke cakes crafted with love in Narayan Peth, Pune.
          </p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <Card className="w-full max-w-md border-[#E8E3DF] shadow-sm">
          <CardContent className="p-7">
            <h1 className="font-heading text-3xl text-[#141E30] mb-1">Welcome back</h1>
            <p className="text-sm text-[#4B5563] mb-5">Sign in to manage your bakery orders.</p>
            <form onSubmit={submit} className="space-y-3.5">
              <div>
                <Label htmlFor="username" className="text-xs">Username</Label>
                <Input
                  id="username" type="text" required autoFocus autoComplete="username"
                  value={username} onChange={(e) => setUsername(e.target.value)}
                  data-testid="login-username-input"
                  className="h-9 border-[#E8E3DF]"
                  placeholder="Username"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-xs">Password</Label>
                <Input
                  id="password" type="password" required autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="h-9 border-[#E8E3DF]"
                  placeholder="••••••••"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-btn"
                className="w-full h-9 bg-[#B86F77] hover:bg-[#A35D65] text-white"
              >
                {loading ? "Signing in…" : "Sign in"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
